import { useEffect, useMemo, useState, useRef } from "react";


import { ensureCsrfToken, getLegacyUrl } from "../../api";

import InvoiceCheckDetailModal from "../../components/InvoiceCheckDetailModal";

import CheckerViewShell from "./CheckerViewShell";

import { openInvoiceData } from "../../utils/invoiceDisplay";

import { openInvoicePdf } from "../../utils/openPdf";


function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function StatusModal({ detail, onClose }) {
  if (!detail) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checker-generated-status-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Status Detail</p>
            <h2 id="checker-generated-status-title">{detail.label}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-note">{detail.message || "No additional message available."}</p>
        <pre className="processor-modal__code">
          {JSON.stringify(detail.data ?? {}, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function TrailModal({ trail, loading, onClose }) {

  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}

export default function CheckerGeneratedView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusDetail, setStatusDetail] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);

  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const summary = useMemo(
    () => ({
      count: rows.length,
      selected: selectedRows.length,
      redFlags: rows.reduce(
        (count, row) =>
          count + Object.values(row.InvoiceCheck || {}).filter((item) => item?.color === "r").length,
        0,
      ),
    }),
    [rows, selectedRows],
  );

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-generated/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Unable to load checker generated invoices. Backend responded with ${response.status}.`,
        );
      }

      if (!payload) {
        throw new Error("Unable to load checker generated invoices. Backend returned a non-JSON response.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load checker generated invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  function toggleRowSelection(invoiceGroupKey) {
    setSelectedRows((current) =>
      current.includes(invoiceGroupKey)
        ? current.filter((item) => item !== invoiceGroupKey)
        : [...current, invoiceGroupKey],
    );
  }

  function toggleAllRows() {
    setSelectedRows((current) => (current.length === rows.length ? [] : rows.map((row) => row.InvoiceGroupKey)));
  }

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.InvoiceId}/`, {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load trail.");
      }
      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  async function handleAction(row, type) {
    setStatus("");
    setError("");
    setActionKey(`${type}-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}d successfully.`);
      setSelectedRows((current) => current.filter((item) => item !== row.InvoiceGroupKey));
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  async function handlePreview(row) {
    setStatus("");
    setError("");
    setActionKey(`preview-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/template-preview/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_keys: [row.InvoiceGroupKey],
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to generate preview.");
      }

      const previewWindow = window.open("", "_blank", "noreferrer");
      if (!previewWindow) {
        throw new Error("Preview window was blocked by the browser.");
      }

      let html = "<html><head><title>Preview</title></head><body style='padding:24px;font-family:Segoe UI,sans-serif;'>";
      Object.entries(payload.template_html || {}).forEach(([key, value]) => {
        html += `<h3>${key}</h3>${value}<hr/>`;
      });
      html += "</body></html>";

      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (requestError) {
      setError(requestError.message || "Unable to generate preview.");
    } finally {
      setActionKey("");
    }
  }

  function openLegacyGeneratedPage() {
    window.localStorage.setItem("activeApprovalSection", "generated");
    window.open(getLegacyUrl("/checker-dashboard/"), "_blank", "noreferrer");
  }

  function refreshPendingActions() {
    loadRows();
  }

  return (
    <CheckerViewShell
      title="Template Generated Invoices"
      intro=""
      metrics={[
        { label: "Queue", value: summary.count },
        
      ]}
      actions={
        <button
          className="ghost-button processor-view__refresh"
          type="button"
          onClick={refreshPendingActions}
        >
          Refresh
        </button>
      }
    >
      <section className="processor-view">
        

        

        <div className="workspace-panel">
          

          {loading ? (
            <p className="config-note">Loading checker generated invoices…</p>
          ) : (
            <div className="config-table processor-table">
              <table>
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Vendor Name</th>
                    <th>Invoice No.</th>
                    <th>Invoice Date</th>
                    <th>Invoice Value</th>
                    <th>Invoice Status</th>
                    <th>Response</th>
                    <th>File</th>
                    <th>Preview</th>
                    <th>Logs</th>
                    
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((row) => (
                      <tr key={row.InvoiceGroupKey}>
                        <td>{row.InvoiceId}</td>
                        <td>{row.VendorName || "-"}</td>
                        <td>{row.InvoiceNo || row.InvNo || "-"}</td>
                        <td>{row.InvoiceDate || row.InvDate || "-"}</td>
                        <td>{formatAmount(row.InvoiceValue || row.TotalAmount)}</td>
                        <td>
                          <div className="processor-status-list">
                            {Object.entries(row.invoiceCheck || {}).length ? (
                              Object.entries(row.invoiceCheck || {}).map(([label, item]) => (
                                <button
                                  key={`${row.invoiceGroupKey}-${label}`}
                                  className={`processor-status-dot processor-status-dot--${item?.color || "o"}`}
                                  type="button"
                                  title={`${label}: ${item?.message || ""}`}
                                  onClick={() =>
                                    setStatusDetail({
                                      label,
                                      message: item?.message,
                                      data: item?.data,
                                      key: item?.key || label,
                                      checkKey: label,
                                      uniqueKey: row.invoiceGroupKey,
                                      color: item?.color || "o",
                                    })
                                  }
                                />
                              ))
                            ) : (
                              <span className="empty-state">No checks</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {row.unique_name ? (
                            <button
                              onClick={() =>
                                openInvoiceData(
                                  `${row.unique_name}.json`
                                )
                              }
                            >
                              Data
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {row.unique_name ? (
                            <button
                              type="button"
                              onClick={() => openInvoicePdf(row.InvoiceId)}
                            >
                              📎
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <button
                            className="ghost-button"
                            type="button"
                            disabled={actionKey === `preview-${row.InvoiceGroupKey}`}
                            onClick={() => handlePreview(row)}
                          >
                            {actionKey === `preview-${row.InvoiceGroupKey}` ? "Previewing..." : "Preview"}
                          </button>
                        </td>
                        <td>
                          <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                            Logs
                          </button>
                        </td>
                        
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12">No invoices found in the checker generated queue.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <InvoiceCheckDetailModal
          detail={statusDetail}
          onClose={() => setStatusDetail(null)}
          onSaved={loadRows}
        />
        <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
      </section>
    </CheckerViewShell>
  );
}
